import type { Account } from '@xinfer/api-client';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CodeBlock,
  CodeTabs,
  CopyButton,
  PageHeader,
  SectionHeader,
  usePageTitle,
  type CodeTab,
} from '@xinfer/ui';
import { Info } from 'lucide-react';

/**
 * Everything needed to make the first request, prefilled.
 *
 * The endpoint is OpenAI-compatible on purpose, so the shortest honest
 * quickstart is "point your existing client at this base URL". Say that
 * plainly rather than inventing a bespoke SDK to document.
 */
export function Quickstart({ account }: { account: Account }) {
  usePageTitle('Quickstart');

  const baseUrl = `${window.location.origin.replace(':5173', ':7000')}/v1`;

  const tabs: CodeTab[] = [
    {
      id: 'curl',
      label: 'curl',
      language: 'shell',
      code: `curl ${baseUrl}/chat/completions \\
  -H "Authorization: Bearer $XINFER_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "mock-v4-flash",
    "messages": [{"role": "user", "content": "hello"}]
  }'`,
    },
    {
      id: 'python',
      label: 'Python',
      language: 'python',
      code: `import os
from openai import OpenAI

client = OpenAI(
    api_key=os.environ["XINFER_API_KEY"],
    base_url="${baseUrl}",
)

stream = client.chat.completions.create(
    model="mock-v4-flash",
    messages=[{"role": "user", "content": "hello"}],
    stream=True,
)
for chunk in stream:
    print(chunk.choices[0].delta.content or "", end="")`,
    },
    {
      id: 'node',
      label: 'Node',
      language: 'javascript',
      code: `import OpenAI from 'openai';

const client = new OpenAI({
  apiKey: process.env.XINFER_API_KEY,
  baseURL: '${baseUrl}',
});

const stream = await client.chat.completions.create({
  model: 'mock-v4-flash',
  messages: [{ role: 'user', content: 'hello' }],
  stream: true,
});

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0]?.delta?.content ?? '');
}`,
    },
  ];

  return (
    <>
      <PageHeader
        title="Quickstart"
        description="The API is OpenAI-compatible, so most existing clients work unmodified."
      />

      <Card>
        <CardHeader>
          <CardTitle>Base URL</CardTitle>
          <CardDescription>
            Point an existing OpenAI client at this and it works — same request shape, same
            streaming chunk format.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-muted/50 px-3 py-2 font-mono text-sm">
            {baseUrl}
          </code>
          <CopyButton value={baseUrl} />
        </CardContent>
      </Card>

      <div className="mt-8">
        <SectionHeader
          title="1. Create a key"
          description="On the API keys page. It is shown once, so store it before closing the dialog."
        />
        <CodeBlock
          language="shell"
          code={`export XINFER_API_KEY="sk-..."   # the key from the reveal dialog`}
        />
      </div>

      <div className="mt-8">
        <SectionHeader title="2. Make a request" description="Streaming, in three clients." />
        <CodeTabs tabs={tabs} />
      </div>

      <div className="mt-8">
        <SectionHeader title="Notes" />
        <div className="space-y-3">
          <Alert>
            <Info />
            <div>
              <AlertTitle>Only two endpoints exist</AlertTitle>
              <AlertDescription>
                <code className="font-mono">/v1/chat/completions</code> and{' '}
                <code className="font-mono">/v1/models</code>. Everything else answers{' '}
                <code className="font-mono">501</code> rather than pretending to work.
              </AlertDescription>
            </div>
          </Alert>

          <Alert>
            <Info />
            <div>
              <AlertTitle>Usage is metered in KV-token-seconds</AlertTitle>
              <AlertDescription>
                Not in output tokens. That matters for long contexts — see Limits for why, and
                Overview for what it has cost so far.
              </AlertDescription>
            </div>
          </Alert>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Account <span className="font-mono">{account.id}</span>
        </p>
      </div>
    </>
  );
}
